import { toast } from 'react-toastify';
import statusModal from '@pages/Forms/components/modals/statusModal';
import SubmissionModal from '@pages/Forms/components/modals/SubmissionModal';
import * as turf from '@turf/turf';
import {
  GeometryTypes,
  LoadingMessages,
  RequestStatus,
  RequestType,
  ResponseStatuses,
  StorageScopes,
} from '@src/utils/enums';
import { useApp, useAquaticResources, useJpa, useModal, useRequest } from '@stores';

import L from 'leaflet';
import { queryClient } from '@api/queryClient';
import { DASHBOARD_QUERY_KEYS } from '@api/services/dashboard';
import { PROJECT_QUERY_KEYS } from '@api/services/project';
import { AQUATIC_RESOURCES_QUERY_KEYS, AquaticResourcesQueries } from '@api/services/aquatic-resources/query';
import { router } from '@src/Router';

const handleSuccess = (signed, requestType, body, store, dispatch, submissionModal) => {
  const { setIsLoading, setIsSavingProgress } = useApp.getState();
  const { openModal } = useModal.getState();
  setIsSavingProgress(false);
  setIsLoading(false);
  if (signed) {
    toast.success(`${requestType} Request has been successfully submitted!`);
    openModal(submissionModal, {
      msg: body?.msg,
      status: body?.status ?? ResponseStatuses.Success,
    });
    router.navigate({ to: '/dashboard' });
  } else {
    toast.success(`${requestType} Request has been successfully saved!`);
    dispatch({ type: 'UPDATE_REQUEST_API_DATA', payload: body?.data });
    dispatch({ type: 'UPDATE_REQUEST_FORM_DATA', payload: body?.data });
    dispatch({ type: 'UPDATE_REQUEST_OBJECT', payload: { requestStatus: RequestStatus.Draft } });
  }
};

const validateAquaticResourceGeometryBoundary = async () => {
  const geometryValidation = await AquaticResourcesQueries.validateAquaticResourceGeometryBoundary();
  const { setAquaticResourceGeometryBoundaryFailures, setAquaticResourceGeometryBoundaryInvalidPages } =
    useAquaticResources.getState();
  setAquaticResourceGeometryBoundaryFailures(geometryValidation?.data?.aquaticResourceIds ?? []);
  setAquaticResourceGeometryBoundaryInvalidPages(geometryValidation?.data?.invalidPages ?? []);
  return geometryValidation;
};

const JDFormDataBundle = {
  name: 'JDFormData',

  getReducer: () => {
    const initialState = {
      jdRequests: [],
      errors: {},
    };

    return (state = initialState, { type, payload }) => {
      switch (type) {
        case 'RESET_JD':
          return initialState;
        case 'UPDATE_JD':
          return {
            ...state,
            jdRequests: [{ ...state.jdRequests[0], ...payload }],
          };
        case 'UPDATE_JD_ERRORS':
          return { ...state, errors: payload };
        default:
          return state;
      }
    };
  },

  selectJDData: (state) => state.JDFormData.jdRequests[0],
  selectJDRequestID: (state) => state.JDFormData.jdRequests?.[0]?.jdRequestID,
  selectJDErrors: (state) => state.JDFormData.errors,

  doFetchJD:
    (projectID, requestID, type, requestType, requestStatus, isGenerate = false, archived) =>
    ({ apiGet, dispatch, store }) => {
      const { setIsLoading } = useApp.getState();
      const { openSecondaryModal } = useModal.getState();
      const { resetToDefaults } = useJpa.getState();
      const { setProjectBounds, setProjectGeometry, setSelectedRequestId } = useRequest.getState();

      // Check if fetching an old request name
      const oldRequest =
        requestType === RequestType.DelineationReport ||
        requestType === requestType.DelineationReportAndJurisdictionalDetermination;
      if (oldRequest) type = '7';

      !isGenerate && setIsLoading(true, `Retrieving ${requestType} Request Information...`);

      store.doResetJDFormData();
      store.doResetRequestData();
      resetToDefaults();

      const uri =
        '/api/JD/getJD?' +
        new URLSearchParams({
          projectID,
          requestID,
        });

      return apiGet(uri, (err, body) => {
        !isGenerate && setIsLoading(false);
        if (!err && body.status === ResponseStatuses.Success) {
          const responseData = { ...body?.data, archived };
          const requestData = responseData?.request;
          const jpaBasicInfo = requestData?.jpaBasicInfo;
          const jdRequestID = requestData?.jdRequests?.[0]?.jdRequestID;

          dispatch({ type: 'UPDATE_REQUEST_API_DATA', payload: responseData });
          dispatch({ type: 'UPDATE_REQUEST_FORM_DATA', payload: responseData });
          dispatch({
            type: 'UPDATE_REQUEST_OBJECT',
            payload: { requestStatus: requestStatus },
          });

          useJpa.setState({ jpaBasicInfo });

          dispatch({
            type: 'UPDATE_JD',
            payload: requestData?.jdRequests?.filter((item) => item.jdRequestID === jdRequestID)[0],
          });
          if (isGenerate) {
            store.doGenerateDraftDocuments();
          } else {
            setProjectGeometry(responseData.location.geometry);

            const geometryType = responseData.location.geometry.type;
            const validTypes = [
              GeometryTypes.LineString,
              GeometryTypes.MultiLineString,
              GeometryTypes.MultiPolygon,
              GeometryTypes.Point,
              GeometryTypes.Polygon,
            ];

            if (geometryType && validTypes.includes(geometryType)) {
              let geometry;

              if (geometryType === GeometryTypes.Point) {
                const point = turf.point(responseData.location.geometry.coordinates);
                geometry = turf.buffer(point, 5, { units: 'miles' });
              } else {
                geometry = responseData.location.geometry;
              }

              const bbox = turf.bbox(geometry);
              const bounds = L.latLngBounds([
                { lat: bbox[1], lng: bbox[0] },
                { lat: bbox[3], lng: bbox[2] },
              ]);
              setProjectBounds(bounds);
            }
            queryClient.invalidateQueries({
              queryKey: [
                PROJECT_QUERY_KEYS.GET_SHARED_CONTACTS,
                store.selectProjectID(),
                store.selectRequestID(),
                store.selectVersion(),
              ],
            });
            setSelectedRequestId(type);
            router.navigate({
              to: '/forms/$id',
              params: { id: String(requestID) },
            });
            store
              .doFetchUserFilesDetails({ componentID: jdRequestID, scope: StorageScopes.ProjectFolder })
              .finally(() => {
                dispatch({ type: 'SET_LOADING_STATE', payload: false });
              });
          }
        } else {
          openSecondaryModal(statusModal, {
            msg: body?.msg ?? err?.msg ?? 'An error occurred while retrieving the request, please try again',
            status: body?.status ?? err?.status ?? 'Request Retrieval Error',
          });
          dispatch({ type: 'SET_LOADING_STATE', payload: false });
        }
      });
    },
  doAddJD:
    (data) =>
    ({ apiPost, dispatch, store }) => {
      const { setIsLoading } = useApp.getState();
      const { openSecondaryModal, closeModal } = useModal.getState();
      const { resetToDefaults } = useJpa.getState();

      const requestType =
        useRequest.getState().selectedRequestId === '1'
          ? RequestType.AquaticResourceDelineationReport
          : RequestType.JurisdictionalDetermination;

      setIsLoading(true, `Creating ${requestType} Request...`);
      resetToDefaults();

      const uri = '/api/JD/addJD';

      return apiPost(uri, data, (err, body) => {
        setIsLoading(false);
        if (!err && body.status === ResponseStatuses.Success) {
          dispatch({
            type: 'UPDATE_REQUEST_API_DATA',
            payload: {
              projectID: body.data.projectID,
              projectVersion: body.data.projectVersion,
              request: { requestID: body.data.requestID, permission: body.data.permission },
            },
          });
          dispatch({
            type: 'UPDATE_REQUEST_FORM_DATA',
            payload: {
              projectID: body.data.projectID,
              projectVersion: body.data.projectVersion,
              request: { requestID: body.data.requestID },
            },
          });

          useJpa.setState({ jpaBasicInfo: body.data.jpaBasicInfo });

          dispatch({
            type: 'UPDATE_REQUEST_OBJECT',
            payload: { requestStatus: RequestStatus.Draft, requestType: requestType, requestID: body.data.requestID },
          });
          dispatch({
            type: 'UPDATE_JD',
            payload: { jdRequestID: body.data.jdRequestID },
          });
          router.navigate({ to: '/forms' });
          store.doFetchUserFilesDetails({ componentID: body.data.jdRequestID, scope: StorageScopes.ProjectFolder });
          closeModal();
          queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD] });
          queryClient.invalidateQueries({ queryKey: [PROJECT_QUERY_KEYS.GET_PROJECT_LOCATION] });
          queryClient.invalidateQueries({
            queryKey: [
              PROJECT_QUERY_KEYS.GET_SHARED_CONTACTS,
              store.selectProjectID(),
              store.selectRequestID(),
              store.selectVersion(),
            ],
          });
        } else {
          openSecondaryModal(statusModal, {
            msg: body?.msg ?? err?.msg ?? 'An error occurred while creating the request, please try again',
            status: body?.status ?? err?.status ?? 'Request Creation Error',
          });
        }
      });
    },
  doUploadAndUpdateJD:
    (data, status, options = {}) =>
    ({ store }) => {
      const { setIsLoading, setIsSavingProgress } = useApp.getState();
      const { openSecondaryModal } = useModal.getState();
      const { selectedRequestId } = useRequest.getState();

      const signed = Boolean(data?.request?.isSubmit);

      setIsSavingProgress(true);
      signed && setIsLoading(true, LoadingMessages.SubmitRequest);

      const requestType =
        selectedRequestId === '1'
          ? RequestType.AquaticResourceDelineationReport
          : RequestType.JurisdictionalDetermination;

      if (store.selectValidFiles()?.length > 0) {
        // Format to FormData
        const formdata = new FormData();
        formdata.append('overwrite', false);
        formdata.append('isAnonymous', false);
        store.selectValidFiles().forEach((item) => {
          const fileMetaData = {
            ProjectID: store.selectProjectID(),
            RequestID: store.selectRequestID(),
            Version: 0,
            Type: status,
            Tags: item?.tags,
          };
          formdata.append('files', item?.file);
          formdata.append('fileMetaData', JSON.stringify(fileMetaData));
        });
        // Upload Files and Save Request
        Promise.all([store.doUploadFiles(formdata)])
          .then((results) => {
            if (results?.[0]?.status === ResponseStatuses.Success) {
              store.doUpdateJD(data, options);
              store.doResetUsersFileData();
              store.doFetchUserFilesDetails({
                componentID: store.selectJDRequestID(),
                scope: StorageScopes.ProjectFolder,
              });
            } else {
              // if any of the files failed to upload
              setIsSavingProgress(false);
              signed && setIsLoading(false);

              store.doResetUsersFileData();
              openSecondaryModal(statusModal, {
                status: 'ERROR! Failed Uploads',
                msg: (
                  <>
                    <p>{results?.[0]?.msg}</p>
                    <p>Retry saving/submitting your {requestType} Request in a few minutes.</p>
                  </>
                ),
                saveText: 'Return to request',
              });
            }
          })
          .catch((e) => {
            console.error(e);
          });
      } else {
        // If no files were selected, just update JD
        store.doUpdateJD(data, options);
      }
    },
  doUpdateJD:
    (data, options = {}) =>
    async ({ apiPut, dispatch, store }) => {
      const { setIsLoading, setIsSavingProgress } = useApp.getState();
      const { openSecondaryModal } = useModal.getState();
      const { selectedRequestId } = useRequest.getState();

      const signed = Boolean(data?.request?.isSubmit);
      const requestId = data?.request?.requestID;
      const { hasArChanges = false } = options;

      const savedDetermination = store.selectRequestAPIData()?.request?.jdRequests?.[0]?.jurisdictionalDetermination;
      const hasJdDeterminationChanged =
        data?.request?.jdRequests?.[0]?.jurisdictionalDetermination !== savedDetermination;

      setIsSavingProgress(true);
      setIsLoading(true, signed ? LoadingMessages.SubmitRequest : LoadingMessages.SaveRequest);

      const requestType =
        selectedRequestId === '1'
          ? RequestType.AquaticResourceDelineationReport
          : RequestType.JurisdictionalDetermination;

      const uri = '/api/JD/updateJD';

      if (signed && requestType === RequestType.AquaticResourceDelineationReport) {
        const geometryValidation = await validateAquaticResourceGeometryBoundary();
        if (geometryValidation?.status === ResponseStatuses.ValidationFailed) {
          setIsSavingProgress(false);
          setIsLoading(false);
          return;
        }
      }

      return apiPut(uri, data, (err, body) => {
        if (
          !err &&
          [ResponseStatuses.Success, ResponseStatuses.SuccessWithWarning, ResponseStatuses.Warning].includes(
            body?.status
          )
        ) {
          handleSuccess(signed, requestType, body, store, dispatch, SubmissionModal);

          const shouldRefreshArValidation =
            hasJdDeterminationChanged || (hasArChanges && requestType === RequestType.AquaticResourceDelineationReport);
          if (shouldRefreshArValidation) {
            void queryClient.invalidateQueries({
              queryKey: [AQUATIC_RESOURCES_QUERY_KEYS.GET_AQUATIC_RESOURCES_VALIDATIONS, requestId],
            });
          }

          if (hasArChanges && requestType === RequestType.AquaticResourceDelineationReport) {
            void queryClient.invalidateQueries({
              queryKey: [AQUATIC_RESOURCES_QUERY_KEYS.GET_AQUATIC_RESOURCES_USACE_DISTRICT_VALIDATION, requestId],
            });
          }

          queryClient.invalidateQueries({
            queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD],
          });

          queryClient.invalidateQueries({
            queryKey: [
              PROJECT_QUERY_KEYS.GET_SHARED_CONTACTS,
              store.selectProjectID(),
              store.selectRequestID(),
              store.selectVersion(),
            ],
          });

          queryClient.invalidateQueries({
            queryKey: [PROJECT_QUERY_KEYS.GET_PROJECT_LOCATION],
          });
        } else {
          setIsSavingProgress(false);
          setIsLoading(false);

          openSecondaryModal(statusModal, {
            msg: body?.msg ?? err?.msg ?? 'An error occurred while saving or submitting the request, please try again',
            status: body?.status ?? err?.status ?? 'Request Save/Submission Error',
          });

          dispatch({
            type: 'UPDATE_JD_ERRORS',
            payload: body?.data,
          });
        }
      });
    },
  doUpdateJDRequest:
    (data) =>
    ({ dispatch, store }) => {
      dispatch({ type: 'UPDATE_JD', payload: data });
      dispatch({ type: 'UPDATE_REQUEST_OBJECT', payload: { jdRequests: [store.selectJDData()] } });
    },
  doResetJDFormData:
    () =>
    ({ dispatch }) => {
      dispatch({ type: 'RESET_JD' });
    },
};

export default JDFormDataBundle;
